import React, { Component } from "react";
import PropTypes from "prop-types";
import cx from "classnames";

import { withTheme } from "@nextiva/next-ui"; // from next-ui
import clearSvg from "../assets/svg-icons/nx-close.svg";
import searchSvg from "../assets/svg-icons/nx-search.svg";
import { createClassNameCombinator } from "utils/styles";
import SvgIcon from "analytics-ui/SvgIcon";
import { NumberKeyCodes as KeyCodes } from "analytics-ui/consts/keyCodes";
import withFocusedState from "analytics-ui/enhancers/withFocusedState";

const themes = {
  default: () => import("./styles/default.module.scss"),
  modern: () => import("./styles/modern.module.scss"),
};

class SearchField extends Component {
  static propTypes = {
    onChange: PropTypes.func,
    value: PropTypes.string,
    placeholder: PropTypes.string,
    blockName: PropTypes.string,
    containerClassName: PropTypes.string,
    inputClassName: PropTypes.string,
    className: PropTypes.string,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    focused: PropTypes.bool,
    onSearch: PropTypes.func,
    onKeyDown: PropTypes.func,
    autoFocus: PropTypes.bool,
    isError: PropTypes.bool,
    disabled: PropTypes.bool,
    classes: PropTypes.object,
    inputRef: PropTypes.oneOfType([
      PropTypes.func,
      PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
    ]),
  };

  static defaultProps = {
    value: "",
    placeholder: "Search...",
    blockName: "nx-searchfield",
    containerClassName: "",
    inputClassName: "",
    className: "",
    onChange: () => {},
    onSearch: () => {},
    onKeyDown: () => {},
    onFocus: () => {},
    onBlur: () => {},
    autoFocus: false,
    isError: false,
    disabled: false,
    focused: false,
    classes: {},
  };

  state = {
    focused: false,
    value: this.props.value,
  };

  removeValue = () => {
    this.props.onChange(null, "");
  };

  handleKeyDown = (event) => {
    const { value, onKeyDown } = this.props;
    onKeyDown(event);

    if (event.keyCode === KeyCodes.ESCAPE) {
      event.preventDefault();
      event.stopPropagation();
      this.removeValue();
    } else if (event.keyCode === KeyCodes.ENTER) {
      this.onSearch(value);
    }
  };

  onFocus = (event) => {
    this.setState({ focused: true });
    this.props.onFocus(event);
  };

  onBlur = (event) => {
    this.setState({ focused: false });
    this.props.onBlur(event);
  };

  onSearch = (value) => {
    this.props.onSearch(value);
  };

  onChange = (event) => {
    const value = event.target.value;
    this.props.onChange(event, value);
  };

  renderSearchOrCloseIcon = () => {
    const { value, blockName, classes, className } = this.props;
    const { focused } = this.state;
    const iconType = value ? "clear" : "search";
    const getClassName = createClassNameCombinator(
      `${blockName}__${iconType}`,
      className && `${className}__${iconType}`,
      classes
    );
    const clearClassName = getClassName("-icon");
    const searchClassName = cx(getClassName("-icon"), {
      [getClassName("-icon--active")]: focused,
    });

    return (
      <button
        className={getClassName("-button")}
        type="button"
        onClick={value ? this.removeValue : undefined}
      >
        <SvgIcon
          className={clearClassName}
          src={clearSvg}
          style={{ display: value ? "block" : "none" }}
        />
        <SvgIcon
          className={searchClassName}
          src={searchSvg}
          style={{ display: !value ? "block" : "none" }}
        />
      </button>
    );
  };

  render() {
    const {
      classes,
      placeholder,
      blockName,
      containerClassName,
      inputClassName,
      isError,
      disabled,
      autoFocus,
      value,
      inputRef,
      className,
      focused: _focused,
      onSearch,
      ...other
    } = this.props;

    const { focused } = this.state;
    const getClassName = createClassNameCombinator(
      blockName,
      className,
      classes
    );

    const fullClassName = cx(
      getClassName(),
      containerClassName,
      getClassName(`--${value ? "filled" : "empty"}`),
      {
        [getClassName("--error")]: isError,
        [getClassName("--active")]: focused,
        [getClassName("--disabled")]: disabled,
      }
    );

    return (
      <div className={fullClassName}>
        <input
          {...other}
          autoFocus={autoFocus}
          className={cx(getClassName("__input"), inputClassName)}
          disabled={disabled}
          onFocus={this.onFocus}
          onBlur={this.onBlur}
          onKeyDown={this.handleKeyDown}
          onChange={this.onChange}
          value={value}
          placeholder={placeholder}
          ref={inputRef}
        />
        {this.renderSearchOrCloseIcon()}
      </div>
    );
  }
}

export default withFocusedState(withTheme(SearchField, { themes }));
